import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TicketDownloadModal from '../components/TicketDownloadModal';

const PaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [pollCount, setPollCount] = useState(0);
  const [showTicket, setShowTicket] = useState(false);

  useEffect(() => {
    console.log('[PaymentStatus] Component mounted');
    console.log('[PaymentStatus] URL params:', window.location.search);
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    try {
      const orderId = searchParams.get('orderId') || sessionStorage.getItem('lastBookingId');
      
      if (!orderId) {
        setError('Order ID not found');
        setStatus('error');
        return;
      }

      console.log('[PaymentStatus] Checking status for order:', orderId);
      console.log('[PaymentStatus] API_BASE_URL:', API_BASE_URL);

      // Try to get PhonePe order status first
      let response;
      try {
        // Check if this is a PhonePe order
        const phonePeUrl = `${API_BASE_URL}/api/phonepe-tsp/status/${orderId}`;
        console.log('[PaymentStatus] Fetching PhonePe status from:', phonePeUrl);
        response = await axios.get(phonePeUrl);
        console.log('[PaymentStatus] PhonePe order found:', response.data);
        
        const state = response.data.data.state;
        console.log('[PaymentStatus] PhonePe state:', state);
        
        // Create a minimal booking object from PhonePe data
        const phonePeBooking = {
          bookingId: response.data.data.bookingId || orderId,
          totalAmount: response.data.data.amount, // Already in rupees from backend
          phonePeStatus: {
            state: state,
            merchantTransactionId: response.data.data.merchantTransactionId,
            orderId: response.data.data.orderId
          }
        };
        
        // Try to get full booking details if bookingId exists
        if (response.data.data.bookingId) {
          try {
            const bookingResponse = await axios.get(`${API_BASE_URL}/api/bookings/${response.data.data.bookingId}`);
            const bookingData = bookingResponse.data.booking;
            
            // Merge PhonePe status into booking data
            bookingData.phonePeStatus = phonePeBooking.phonePeStatus;
            setBooking(bookingData);
            console.log('[PaymentStatus] Full booking data loaded:', bookingData);
          } catch (bookingError) {
            console.log('[PaymentStatus] Could not fetch booking, using PhonePe data only:', bookingError.message);
            setBooking(phonePeBooking);
          }
        } else {
          // No booking ID, use PhonePe data only
          console.log('[PaymentStatus] No bookingId in PhonePe order, cannot show ticket');
          setBooking(phonePeBooking);
        }
        
        // Set status based on state
        if (state === 'SUCCESS') {
          setStatus('success');
          // Only auto-show ticket if we have full booking data
          if (response.data.data.bookingId) {
            setShowTicket(true);
          }
          sessionStorage.removeItem('lastBookingId');
          return;
        } else if (state === 'FAILED' || state === 'EXPIRED') {
          setStatus('failed');
          sessionStorage.removeItem('lastBookingId');
          return;
        } else if (state === 'PENDING' || state === 'INITIATED') {
          setStatus('pending');
          if (pollCount < 20) {
            setPollCount(pollCount + 1);
            setTimeout(checkPaymentStatus, 3000);
          } else {
            setStatus('timeout');
          }
          return;
        } else {
          // Unknown state
          console.log('[PaymentStatus] Unknown state:', state);
          setStatus('pending');
          return;
        }
      } catch (phonePeError) {
        console.log('[PaymentStatus] Not a PhonePe order, trying regular booking lookup');
      }

      // Fallback to regular booking lookup
      response = await axios.get(`${API_BASE_URL}/api/bookings/${orderId}`);

      const bookingData = response.data.booking;
      setBooking(bookingData);

      console.log('[PaymentStatus] Booking data:', bookingData);

      // Check PhonePe status if available
      if (bookingData.phonePeStatus) {
        const state = bookingData.phonePeStatus.state;
        console.log('[PaymentStatus] PhonePe state:', state);
        
        if (state === 'SUCCESS') {
          setStatus('success');
          // Only auto-show ticket if we have full booking data with ticketTypeId
          if (bookingData.ticketTypeId) {
            setShowTicket(true);
          }
          sessionStorage.removeItem('lastBookingId');
        } else if (state === 'FAILED' || state === 'EXPIRED') {
          setStatus('failed');
          sessionStorage.removeItem('lastBookingId');
        } else if (state === 'PENDING' || state === 'INITIATED') {
          setStatus('pending');
          // Poll for status update (max 20 times = 1 minute)
          if (pollCount < 20) {
            setPollCount(pollCount + 1);
            setTimeout(checkPaymentStatus, 3000);
          } else {
            setStatus('timeout');
          }
        }
      } else {
        // Legacy payment status
        if (bookingData.status === 'confirmed') {
          setStatus('success');
          // Only auto-show ticket if we have full booking data with ticketTypeId
          if (bookingData.ticketTypeId) {
            setShowTicket(true);
          }
          sessionStorage.removeItem('lastBookingId');
        } else if (bookingData.status === 'failed' || bookingData.status === 'cancelled') {
          setStatus('failed');
          sessionStorage.removeItem('lastBookingId');
        } else {
          setStatus('pending');
          if (pollCount < 20) {
            setPollCount(pollCount + 1);
            setTimeout(checkPaymentStatus, 3000);
          } else {
            setStatus('timeout');
          }
        }
      }
    } catch (error) {
      console.error('[PaymentStatus] Error checking payment status:', error);
      console.error('[PaymentStatus] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError(error.response?.data?.message || error.message || 'Unknown error occurred');
      setStatus('error');
    }
  };

  // Debug render
  console.log('[PaymentStatus] Rendering with status:', status);
  console.log('[PaymentStatus] Booking data:', booking);
  console.log('[PaymentStatus] Error:', error);

  // Fallback if something goes wrong
  if (!status || status === '') {
    console.log('[PaymentStatus] No status set, defaulting to loading');
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Loading payment status...</h2>
        <p>If this persists, please check the browser console for errors.</p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {status === 'loading' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <h2 style={{ color: '#2d3748', marginBottom: '10px' }}>Checking Payment Status...</h2>
            <p style={{ color: '#64748b' }}>Please wait while we verify your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>✅</div>
            <h2 style={{ color: '#10b981', marginBottom: '10px' }}>Payment Successful!</h2>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>
              Your booking has been confirmed
            </p>
            {booking && (
              <div style={{
                background: '#f0fdf4',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <p style={{ marginBottom: '8px' }}><strong>Booking ID:</strong> {booking.bookingId}</p>
                <p style={{ marginBottom: '8px' }}><strong>Amount:</strong> ₹{booking.totalAmount}</p>
                {booking.phonePeStatus && (
                  <p style={{ marginBottom: '8px', fontSize: '12px', wordBreak: 'break-all' }}>
                    <strong>Transaction ID:</strong> {booking.phonePeStatus.merchantTransactionId}
                  </p>
                )}
                <p style={{ marginBottom: '0' }}><strong>Status:</strong> <span style={{ color: '#10b981' }}>Confirmed</span></p>
                {!booking.ticketTypeId && (
                  <p style={{ marginTop: '12px', fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                    Note: This is a test payment. No booking details available.
                  </p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              {booking.ticketTypeId && (
                <button
                  onClick={() => setShowTicket(true)}
                  style={{
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  📥 View Ticket
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                style={{
                  background: 'linear-gradient(45deg, #10b981, #059669)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  flex: booking.ticketTypeId ? 1 : '100%'
                }}
              >
                Back to Home
              </button>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>❌</div>
            <h2 style={{ color: '#ef4444', marginBottom: '10px' }}>Payment Failed</h2>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>
              Your payment could not be processed. Please try again.
            </p>
            {booking && (
              <div style={{
                background: '#fef2f2',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <p style={{ marginBottom: '8px' }}><strong>Booking ID:</strong> {booking.bookingId}</p>
                <p style={{ marginBottom: '0' }}><strong>Status:</strong> <span style={{ color: '#ef4444' }}>Failed</span></p>
              </div>
            )}
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'linear-gradient(45deg, #ef4444, #dc2626)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Try Again
            </button>
          </>
        )}

        {status === 'pending' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #f59e0b',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <h2 style={{ color: '#f59e0b', marginBottom: '10px' }}>Payment Pending</h2>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>
              Your payment is being processed. Please wait...
            </p>
            {booking && (
              <div style={{
                background: '#fffbeb',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <p style={{ marginBottom: '8px' }}><strong>Booking ID:</strong> {booking.bookingId}</p>
                <p style={{ marginBottom: '0' }}><strong>Status:</strong> <span style={{ color: '#f59e0b' }}>Pending</span></p>
              </div>
            )}
            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '20px' }}>
              This page will automatically update when payment is confirmed
            </p>
            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
              Checking... ({pollCount}/20)
            </p>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>⏱️</div>
            <h2 style={{ color: '#f59e0b', marginBottom: '10px' }}>Payment Status Unknown</h2>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>
              We're still processing your payment. Please check your booking status later.
            </p>
            {booking && (
              <div style={{
                background: '#fffbeb',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <p style={{ marginBottom: '8px' }}><strong>Booking ID:</strong> {booking.bookingId}</p>
                <p style={{ marginBottom: '0' }}>Save this ID to check your booking status</p>
              </div>
            )}
            <button
              onClick={() => navigate('/')}
              style={{
                background: '#64748b',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Go Home
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>⚠️</div>
            <h2 style={{ color: '#ef4444', marginBottom: '10px' }}>Error</h2>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>{error || 'Something went wrong'}</p>
            <button
              onClick={() => navigate('/')}
              style={{
                background: '#64748b',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Go Home
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Ticket Modal */}
      {showTicket && booking && (
        <TicketDownloadModal
          isOpen={showTicket}
          onClose={() => setShowTicket(false)}
          booking={booking}
          mode="display"
        />
      )}
    </div>
  );
};

export default PaymentStatus;
